import React from 'react'
import { Box, type BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

interface SelectorBoxProps extends Omit<BoxProps, 'component'> {
  disabled?: boolean
  error?: boolean
  hasBorder?: boolean
  isOpen?: boolean
}

const StyledBox = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'disabled' && prop !== 'error' && prop !== 'hasBorder' && prop !== 'isOpen',
})<SelectorBoxProps>(({ theme, disabled, error = false, hasBorder = true, isOpen = false }) => ({
  backgroundColor: disabled ? theme.palette.action.disabledInput : 'white',
  border: 'none',
  borderRadius: '4px',
  padding: theme.spacing(1),
  minHeight: '40px',
  display: 'flex',
  alignItems: 'center',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontFamily: theme.typography.body2.fontFamily,
  boxShadow: hasBorder
    ? disabled
      ? `inset 0 0 0 1px ${theme.palette.input.disabled}`
      : error
        ? `inset 0 0 0 2px ${theme.palette.error.main}`
        : isOpen
          ? `inset 0 0 0 2px ${theme.palette.input.hover}, 0 0 0 2px ${theme.palette.input.focus}`
          : `inset 0 0 0 1px ${theme.palette.input.default}`
    : 'none',
  transition: theme.transitions.create(['box-shadow']),
  opacity: disabled ? 0.7 : 1,

  '&:hover': {
    boxShadow: hasBorder
      ? disabled
        ? `inset 0 0 0 1px ${theme.palette.text.disabled}`
        : isOpen
          ? `inset 0 0 0 2px ${theme.palette.input.hover}, 0 0 0 2px ${theme.palette.input.focus}`
          : `inset 0 0 0 2px ${theme.palette.input.hover}`
      : 'none',
  },

  '&:focus-visible': {
    boxShadow: hasBorder
      ? `inset 0 0 0 2px ${theme.palette.input.hover}, 0 0 0 2px ${theme.palette.input.focus}`
      : `0 0 0 2px ${theme.palette.input.focus}`,
    outline: 'none',
  },
}))

const SelectorBox = React.forwardRef<HTMLDivElement, SelectorBoxProps>(
  ({ disabled = false, error = false, hasBorder = true, isOpen = false, ...props }, ref) => {
    return (
      <StyledBox
        ref={ref}
        disabled={disabled}
        error={error}
        hasBorder={hasBorder}
        isOpen={isOpen}
        {...props}
      />
    )
  },
)

export default SelectorBox
